import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import PageLoader from '../../components/PageLoader';
import { useAuth } from '../../context/AuthContext';

interface TranscriptMessage {
  role: string;
  content: string;
  timestamp?: string;
}

interface TranscriptResponse {
  messages: TranscriptMessage[];
  raw_transcript?: string | null;
  call_direction?: 'inbound' | 'outbound';
  timestamp?: string | null;
}

const roleLabel = (role: string): string => {
  const normalized = (role || '').toLowerCase();
  if (['assistant', 'agent', 'ai', 'bot'].includes(normalized)) return 'AI Agent';
  if (['user', 'customer', 'human'].includes(normalized)) return 'Human';
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const isAgentRole = (role: string): boolean => {
  const normalized = (role || '').toLowerCase();
  return ['assistant', 'agent', 'ai', 'bot'].includes(normalized);
};

const TranscriptViewerPage: React.FC = () => {
  const { callSummaryId } = useParams<{ callSummaryId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptResponse | null>(null);

  const canViewTranscripts = Boolean(user?.can_view_transcripts);

  useEffect(() => {
    if (authLoading) return;
    if (!callSummaryId) {
      setError('Missing call summary ID');
      setLoading(false);
      return;
    }
    if (!canViewTranscripts) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadTranscript = async () => {
      try {
        setLoading(true);
        const response = await api.get<TranscriptResponse>(`/calls/${callSummaryId}/transcript`);
        if (!cancelled) {
          setTranscript(response.data);
          setError(null);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Failed to load transcript');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTranscript();
    return () => {
      cancelled = true;
    };
  }, [authLoading, canViewTranscripts, callSummaryId]);

  const messages = useMemo(() => transcript?.messages ?? [], [transcript]);

  if (authLoading || loading) {
    return <PageLoader message="Loading transcript..." />;
  }

  if (!canViewTranscripts) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Access denied. Your account does not have permission to view transcripts.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-cyan-400" />
              Transcript Viewer
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              {transcript?.timestamp ? `Call time: ${new Date(transcript.timestamp).toLocaleString()}` : 'Full call transcript'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800/60"
            >
              Back
            </button>
            <Link
              to="/dashboard"
              className="rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-500/20"
            >
              Dashboard
            </Link>
          </div>
        </div>

        <div className="rounded-xl border border-slate-700/50 bg-slate-900/50 backdrop-blur p-4 max-h-[72vh] overflow-y-auto custom-scrollbar space-y-3">
          {messages.length > 0 ? (
            messages.map((msg, idx) => {
              const agent = isAgentRole(msg.role);
              return (
                <div key={`${msg.role}-${idx}`} className={`flex ${agent ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 border ${
                    agent
                      ? 'bg-slate-800/80 border-slate-700/70 text-slate-100'
                      : 'bg-cyan-500/10 border-cyan-500/40 text-cyan-100'
                  }`}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">
                        {roleLabel(msg.role)}
                      </span>
                      {msg.timestamp && (
                        <span className="text-[10px] text-slate-400">{msg.timestamp}</span>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              );
            })
          ) : transcript?.raw_transcript ? (
            <div className="rounded-lg border border-slate-700/60 bg-slate-800/40 p-3">
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{transcript.raw_transcript}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No transcript content is available for this call.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptViewerPage;
